import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/components/ui/use-toast' 
import { isAxiosError } from '@/api/business'
import Button from '@/components/Button'
import { ResearchedProduct } from '@/types/MarketResearch'
import { Box, Package2, User } from 'lucide-react'
import { deleteResearchedProduct } from '@/api/business/marketResearch'
import { ResearchedProductKeys } from '@/queries/useResearchedProductQuery'

interface DeleteProductMarketResearchDialoadProps { 
    isOpen: boolean 
    onClose: () => void
    dataResearchedProducts: ResearchedProduct,
}

const DeleteProductMarketResearchDiaload = ({ isOpen, onClose, dataResearchedProducts }: DeleteProductMarketResearchDialoadProps) => {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    const { mutate: mutateDelete, isPending: mutateLoading } = useMutation({
        mutationFn: deleteResearchedProduct,
        onSuccess: () => { 
            queryClient.invalidateQueries({ queryKey: ResearchedProductKeys.details() })
            onClose()
            toast({
                title: 'Produto excluido com sucesso',
            })
        },
        onError: (err) => {
            let errorMessage =
                'Não foi possível excluir o produto. Tente novamente mais tarde.'

            if (isAxiosError(err)) {
                errorMessage = err.response?.data.message || errorMessage
            }

            toast({
                title: 'Erro ao excluir produto na pesquisa',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    })


    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Excluir Produto</DialogTitle>
                </DialogHeader>
                <div className="pt-4">
                    <p>
                        Realmente deseja excluir o seguinte lançamento?
                    </p>
                    <div className="space-y-2 mt-4 border rounded-lg p-2"> 
                        <div className="flex gap-1.5">
                            <User className='text-gray-600' size={14} />
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-900 font-bold truncate">{dataResearchedProducts.DS_NOME_FANTASIA}</p>
                            </div>
                        </div>

                        <div className="flex gap-1.5">
                            <Box className='text-gray-600' size={14} />
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-900 font-medium truncate ">{dataResearchedProducts.DS_PRODUTO}</p>
                            </div>
                        </div>  
    
                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100">
                            <div>
                                <p className="text-xs text-gray-500 mb-0.5">Compra</p>
                                <p className="text-sm font-bold text-red-600">
                                    R$ {(dataResearchedProducts.VL_PRECO_COMPRA).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-0.5">Venda</p>
                                <p className="text-sm font-bold text-green-600">
                                    R$ {(dataResearchedProducts.VL_PRECO_VENDA).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </p>
                            </div>
                        </div>
    
                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100">
                            <div className="flex gap-1.5">
                                <Package2  className='text-gray-600 mt-0.5' size={14} />
                                <p className="text-xs text-gray-900 font-semibold">{dataResearchedProducts.QTD_VOLUME_COMPRA} un.</p>
                            </div>  
                        </div>
                    </div>
                </div>
                <DialogFooter className='gap-2 grid grid-flow-col'>
                    <Button onClick={onClose} variant="outline">
                        Cancelar
                    </Button>
                    <Button
                        onClick={() => mutateDelete({ SK_PRODUTOS_PESQUISADOS: dataResearchedProducts.SK_PRODUTOS_PESQUISADOS })}
                        isLoading={mutateLoading}
                    >
                        Confirmar
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

export default DeleteProductMarketResearchDiaload
